import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    LayoutDashboard,
    Plus,
    X,
    Save,
    ChevronDown,
    BarChart2,
    PieChart,
    LineChart,
    Activity,
    Table2,
    Hash,
    Loader2,
    Layers,
    Info,
    Check,
    Gauge,
    Grid,
    Sliders,
} from "lucide-react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart as RePieChart,
    Pie,
    Cell,
    LineChart as ReLineChart,
    Line,
    AreaChart as ReAreaChart,
    Area,
    Legend,
    ReferenceLine,
} from "recharts";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import type { ResponsiveLayouts } from "react-grid-layout/legacy";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

// ─── Chart colors (same as VisualizePage) ────────────────────────────────────
const COLORS = ["#f97316", "#3b82f6", "#10b981", "#a855f7", "#ef4444", "#06b6d4", "#84cc16", "#f59e0b", "#ec4899", "#6366f1"];

// ─── Types ────────────────────────────────────────────────────────────────────

type GridItem = { i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number };

interface AvailableWidget {
    id: number;
    name: string;
    description?: string;
    chart_type: string;
    query_definition?: any;
    visualization_config?: any;
}

interface CanvasTile {
    instanceKey: string;
    widget: AvailableWidget;
    queryResult: any;   // raw { columns, rows } from /analytics/query
    loading: boolean;   // true while fetching data
}

// ─── Chart type helpers ───────────────────────────────────────────────────────

const CHART_TYPE_COLOR: Record<string, string> = {
    bar: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    horizontal_bar: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    pie: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    donut: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    line: "bg-green-500/10 text-green-400 border-green-500/20",
    area: "bg-teal-500/10 text-teal-400 border-teal-500/20",
    table: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    stat: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    metric: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    heatmap: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    heat_map: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    gantt: "bg-orange-500/10 text-orange-400 border-orange-500/20",
};

const CHART_TYPE_ICON: Record<string, JSX.Element> = {
    bar: <BarChart2 size={11} />,
    horizontal_bar: <BarChart2 size={11} />,
    pie: <PieChart size={11} />,
    donut: <PieChart size={11} />,
    line: <LineChart size={11} />,
    area: <Activity size={11} />,
    table: <Table2 size={11} />,
    stat: <Hash size={11} />,
    metric: <Hash size={11} />,
    gauge: <Gauge size={11} />,
    heatmap: <Grid size={11} />,
    heat_map: <Grid size={11} />,
    gantt: <Sliders size={11} />,
};

function badgeClass(t: string) { return CHART_TYPE_COLOR[t?.toLowerCase()] ?? "bg-gray-500/10 text-gray-400 border-gray-500/20"; }
function badgeIcon(t: string) { return CHART_TYPE_ICON[t?.toLowerCase()] ?? <BarChart2 size={11} />; }
function uid() { return `tile-${Date.now()}-${Math.floor(Math.random() * 1e6)}`; }

const AGG_LABEL_MAP: Record<string, string> = {
    count: "Count",
    sum: "Sum",
    avg: "Average",
    min: "Min",
    max: "Max"
};

function formatFieldName(field: string): string {
    if (!field) return "";
    return field
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function safeParse(val: any): any {
    if (!val) return null;
    if (typeof val === "string") {
        try { return JSON.parse(val); } catch { return null; }
    }
    return val;
}

function getAxisLabels(widget: any) {
    const queryDef = safeParse(widget?.query_definition);
    const visConfig = safeParse(widget?.visualization_config) || {};

    const xField: string = queryDef?.axis?.x_axis?.field || "";
    const yAggType: string = queryDef?.axis?.y_axis?.[0]?.type || "";
    const yAggLabel: string = queryDef?.axis?.y_axis?.[0]?.label || "";

    const xAxisLabel =
        visConfig.x_axis_label ||
        formatFieldName(xField) ||
        "";

    const yAxisLabel =
        visConfig.y_axis_label ||
        yAggLabel ||
        AGG_LABEL_MAP[yAggType] ||
        (yAggType ? formatFieldName(yAggType) : "");

    return { xAxisLabel: formatFieldName(xAxisLabel), yAxisLabel: formatFieldName(yAxisLabel) };
}

// ─── pivotData — EXACT copy from VisualizePage ────────────────────────────────

function pivotData(data: any) {
    if (!data) return null;
    const columns = data.columns || [];
    const rows = data.rows || [];
    if (columns.length !== 3 || rows.length === 0) return data;

    const valueIsNumeric = rows.some((r: any) => Number.isFinite(Number(r[2])));
    if (!valueIsNumeric) return data;

    const col0Keys = rows.map((r: any) => String(r[0] ?? ""));
    const uniqueDims = new Set(col0Keys);
    if (uniqueDims.size === rows.length) return data;

    const allCategories: string[] = [];
    const catSeen = new Set<string>();
    for (const row of rows) {
        const cat = String(row[1] ?? "");
        if (!catSeen.has(cat)) { catSeen.add(cat); allCategories.push(cat); }
    }
    const categorySet = allCategories.slice(0, 10);

    const dimOrder: any[] = [];
    const dimSeen = new Set<string>();
    for (const row of rows) {
        const key = String(row[0] ?? "");
        if (!dimSeen.has(key)) { dimSeen.add(key); dimOrder.push(row[0]); }
    }

    const lookup: Record<string, Record<string, number>> = {};
    for (const row of rows) {
        const dimKey = String(row[0] ?? "");
        const cat = String(row[1] ?? "");
        if (!lookup[dimKey]) lookup[dimKey] = {};
        lookup[dimKey][cat] = Number(row[2]) || 0;
    }

    return {
        columns: [columns[0], ...categorySet],
        rows: dimOrder.map(dim => {
            const dimKey = String(dim ?? "");
            return [dim, ...categorySet.map(cat => lookup[dimKey]?.[cat] ?? 0)];
        }),
        _pivoted: true,
    };
}

function formatCell(val: any) {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") return JSON.stringify(val);
    if (typeof val === "number") return val.toLocaleString();
    return String(val);
}

function normalizeChartType(t: string): "pie" | "line" | "area" | "bar" {
    const s = t.toLowerCase();
    if (s === "pie" || s === "donut") return "pie";
    if (s === "area") return "area";
    if (s === "line" || s === "timeline") return "line";
    return "bar";
}

// ─── Gauge SVG Chart ──────────────────────────────────────────────────────
function GaugeChart({ value, label, max: propMax }: { value: number; label?: string; max?: number }) {
    const displayValue = typeof value === "number" ? value : 0;

    const max = propMax || (() => {
        if (displayValue <= 0) return 100;
        const mag = Math.pow(10, Math.floor(Math.log10(Math.max(displayValue, 1))));
        return Math.ceil(displayValue / mag) * mag * 2;
    })();

    const pct = Math.min(Math.max(displayValue / max, 0), 1);
    const W = 240, H = 150;
    const cx = W / 2, cy = 130;
    const R = 90, trackW = 18;

    const toXY = (r: number, p: number): [number, number] => {
        const a = Math.PI * (1 - p);
        return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
    };

    const arcD = (r: number, p1: number, p2: number, sweep = 1) => {
        const [x1, y1] = toXY(r, p1);
        const [x2, y2] = toXY(r, p2);
        const large = (p2 - p1) > 0.5 ? 1 : 0;
        return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} ${sweep} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
    };

    const zoneColor = (p: number) =>
        p < 0.4 ? "#0d9488" : p < 0.7 ? "#f59e0b" : "#ef4444";

    const ticks = Array.from({ length: 21 }, (_, i) => i / 20);
    const [lx, ly] = toXY(R + trackW / 2 + 12, 0);
    const [rx2, ry2] = toXY(R + trackW / 2 + 12, 1);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
            {/* Background track */}
            <path d={arcD(R, 0, 1)} fill="none" stroke="#1f2937" strokeWidth={trackW} strokeLinecap="round" />
            {/* Zone bands (dimmed) */}
            <path d={arcD(R, 0, 0.4)} fill="none" stroke="#0d9488" strokeWidth={trackW} opacity={0.2} />
            <path d={arcD(R, 0.4, 0.7)} fill="none" stroke="#f59e0b" strokeWidth={trackW} opacity={0.2} />
            <path d={arcD(R, 0.7, 1)} fill="none" stroke="#ef4444" strokeWidth={trackW} opacity={0.2} />
            {/* Value fill */}
            {pct > 0 && <path d={arcD(R, 0, pct)} fill="none" stroke={zoneColor(pct)} strokeWidth={trackW} strokeLinecap="round" />}
            {/* Zone dividers */}
            {[0.4, 0.7].map((p) => {
                const [ox, oy] = toXY(R - trackW / 2 - 1, p);
                const [ix, iy] = toXY(R + trackW / 2 + 1, p);
                return <line key={p} x1={ox} y1={oy} x2={ix} y2={iy} stroke="#0f172a" strokeWidth={2} />;
            })}
            {/* Ticks */}
            {ticks.map((t, i) => {
                const [ox, oy] = toXY(R - trackW / 2 + 2, t);
                const [ix, iy] = toXY(R - trackW / 2 + (i % 5 === 0 ? 9 : 5), t);
                return <line key={i} x1={ox} y1={oy} x2={ix} y2={iy} stroke="#374151" strokeWidth={i % 5 === 0 ? 2 : 1} />;
            })}
            {/* Center text */}
            <text x={cx} y={cy - 14} textAnchor="middle" fill="#6b7280" fontSize={10} fontWeight={500}>
                {label || "Value"}
            </text>
            <text x={cx} y={cy + 12} textAnchor="middle" fill={zoneColor(pct)} fontSize={22} fontWeight={700} fontFamily="monospace">
                {displayValue.toLocaleString()}
            </text>
            {/* Min/Max */}
            <text x={lx} y={ly + 4} textAnchor="middle" fill="#4b5563" fontSize={8}>0</text>
            <text x={rx2} y={ry2 + 4} textAnchor="middle" fill="#4b5563" fontSize={8}>{max.toLocaleString()}</text>
        </svg>
    );
}

function HeatmapChart({ pivoted, xAxisLabel, yAxisLabel }: { pivoted: any; xAxisLabel?: string; yAxisLabel?: string }) {
    if (!pivoted) return <div className="text-gray-500 text-xs p-4">No data returned.</div>;

    const columns = pivoted.columns || [];
    const rows = pivoted.rows || [];

    if (columns.length === 0 || rows.length === 0) {
        return <div className="text-gray-500 text-xs p-4">No data available.</div>;
    }

    const hasSplit = columns.length > 2;
    const yCategories = hasSplit ? columns.slice(1) : [columns[1] || "Value"];
    const xCategories = rows.map((r: any) => String(r[0] ?? ""));

    const maxVal = (() => {
        let max = 0;
        rows.forEach((row: any) => {
            const values = hasSplit ? row.slice(1) : [row[1]];
            values.forEach((v: any) => {
                const val = Number(v) || 0;
                if (val > max) max = val;
            });
        });
        return max || 1;
    })();

    return (
        <div className="flex flex-col h-full w-full justify-between p-1">
            <div className="relative flex-1 flex min-h-0 min-w-0 gap-3 mt-1">
                {/* Y-Axis labels (Bucket Values) */}
                <div className="flex flex-col justify-around text-right pr-1 select-none min-w-[50px] max-w-[90px] truncate text-[9px] font-semibold text-gray-400">
                    {xCategories.map((xCat: any) => (
                        <div key={xCat} className="truncate py-0.5" title={String(xCat)}>
                            {String(xCat)}
                        </div>
                    ))}
                </div>

                {/* Grid */}
                <div className="flex-1 flex flex-col min-w-0">
                    <div className="flex-1 flex flex-col justify-around gap-1 min-h-0">
                        {xCategories.map((xCat: any, xIdx: number) => (
                            <div key={xCat} className="flex-1 flex gap-1 min-h-0">
                                {yCategories.map((yCat: any, yIdx: number) => {
                                    const rawVal = hasSplit ? rows[xIdx][yIdx + 1] : rows[xIdx][1];
                                    const value = Number(rawVal) || 0;
                                    const pct = maxVal > 0 ? value / maxVal : 0;

                                    const cellStyle = {
                                        backgroundColor: value > 0
                                            ? `rgba(249, 115, 22, ${0.12 + pct * 0.88})`
                                            : "rgba(31, 41, 55, 0.25)",
                                        border: value > 0
                                            ? `1px solid rgba(249, 115, 22, ${0.2 + pct * 0.6})`
                                            : "1px solid rgba(75, 85, 99, 0.2)"
                                    };

                                    return (
                                        <div
                                            key={String(yCat)}
                                            className="flex-1 rounded cursor-pointer transition-all duration-150 hover:scale-[1.03] relative flex items-center justify-center group"
                                            style={cellStyle}
                                            title={`${xCat} / ${yCat}: ${value}`}
                                        >
                                            {value > 0 && (
                                                <span className={`text-[9px] font-bold select-none ${pct > 0.5 ? "text-white" : "text-orange-400"}`}>
                                                    {value}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* X-Axis labels (Split Series) */}
                    <div className="flex gap-1 pt-1.5 select-none h-4 items-center">
                        {yCategories.map((yCat: any) => (
                            <div key={yCat} className="flex-1 text-center text-[9px] font-semibold text-gray-400 truncate px-0.5" title={String(yCat)}>
                                {String(yCat)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* Legend / Footer */}
            <div className="flex items-center justify-between border-t border-gray-800/40 pt-1 mt-1 text-[8px] text-gray-500 font-medium px-0.5 shrink-0">
                <div className="flex items-center gap-2 truncate">
                    {xAxisLabel && <span className="truncate">X: {xAxisLabel}</span>}
                    {yAxisLabel && <span className="truncate">Y: {yAxisLabel}</span>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <span>Density</span>
                    <div className="h-1.5 w-8 rounded-sm bg-gradient-to-r from-orange-500/20 to-orange-500 border border-orange-500/30" />
                    <span className="font-mono text-orange-400 font-semibold">{maxVal}</span>
                </div>
            </div>
        </div>
    );
}

function GanttChart({ pivoted, xAxisLabel, yAxisLabel }: { pivoted: any; xAxisLabel?: string; yAxisLabel?: string }) {
    if (!pivoted) return <div className="text-gray-500 text-xs p-4">No data returned.</div>;

    const columns = pivoted.columns || [];
    const rows = pivoted.rows || [];

    if (columns.length === 0 || rows.length === 0) {
        return <div className="text-gray-500 text-xs p-4">No data available.</div>;
    }

    const { tasks, timelineStart, timelineEnd } = useMemo(() => {
        const list: { name: string; start: number; end: number }[] = [];
        let absoluteMin = Infinity;
        let absoluteMax = -Infinity;

        rows.forEach((row: any) => {
            const name = String(row[0] ?? "Unnamed Task");
            let start = new Date(row[1]).getTime();
            let end = new Date(row[2]).getTime();

            if (isNaN(start)) start = Date.now();
            if (isNaN(end)) end = start + 86400000;

            if (end < start) end = start + 86400000;

            if (start < absoluteMin) absoluteMin = start;
            if (end > absoluteMax) absoluteMax = end;

            list.push({ name, start, end });
        });

        if (absoluteMin === Infinity) absoluteMin = Date.now();
        if (absoluteMax === -Infinity) absoluteMax = Date.now() + 30 * 24 * 60 * 60 * 1000;

        const range = absoluteMax - absoluteMin;
        const padding = range > 0 ? range * 0.05 : 24 * 60 * 60 * 1000;
        const timelineStart = absoluteMin - padding;
        const timelineEnd = absoluteMax + padding;

        return { tasks: list, timelineStart, timelineEnd };
    }, [rows]);

    const numIntervals = 6;
    const timeIntervals = useMemo(() => {
        const arr = [];
        const step = (timelineEnd - timelineStart) / (numIntervals - 1 || 1);
        for (let i = 0; i < numIntervals; i++) {
            const time = timelineStart + i * step;
            arr.push(new Date(time));
        }
        return arr;
    }, [timelineStart, timelineEnd]);

    const formatDate = (date: Date) => {
        return date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric"
        });
    };

    const duration = timelineEnd - timelineStart;

    return (
        <div className="flex flex-col h-full w-full select-none justify-between p-1 bg-gray-950/20 border border-gray-800 rounded-xl overflow-hidden">
            {/* Gantt Header */}
            <div className="flex border-b border-gray-800 bg-gray-900/40 select-none text-[8px] font-bold text-gray-400 py-1 shrink-0">
                <div className="w-[100px] pl-2">Task</div>
                <div className="flex-1 relative flex justify-between px-1">
                    {timeIntervals.map((date, idx) => (
                        <span key={idx} className="-translate-x-1/2 text-center select-none first:translate-x-0 last:translate-x-[-100%]">
                            {formatDate(date)}
                        </span>
                    ))}
                </div>
            </div>

            {/* Gantt Body */}
            <div className="flex-1 overflow-y-auto custom-scroll min-h-0 relative py-0.5 divide-y divide-gray-900/30">
                {tasks.map((task, idx) => {
                    const leftPct = ((task.start - timelineStart) / duration) * 100;
                    const widthPct = ((task.end - task.start) / duration) * 100;

                    return (
                        <div key={idx} className="flex items-center min-h-[26px] group py-0.5">
                            {/* Task Name */}
                            <div className="w-[100px] text-[9px] font-bold text-gray-300 truncate pr-1 pl-2 select-none" title={task.name}>
                                {task.name}
                            </div>

                            {/* Timeline Bar Space */}
                            <div className="flex-1 relative h-5 border-l border-gray-800/40 bg-gray-900/5 flex items-center">
                                {/* Grid lines background overlay */}
                                <div className="absolute inset-0 flex justify-between pointer-events-none">
                                    {timeIntervals.map((_, gIdx) => (
                                        <div key={gIdx} className="h-full border-l border-gray-800/30" />
                                    ))}
                                </div>

                                {/* Gantt Bar */}
                                <div
                                    className="absolute h-3 rounded bg-gradient-to-r from-orange-500 to-orange-600 border border-orange-400/30 flex items-center justify-center text-[7px] font-bold text-white shadow-md cursor-pointer hover:scale-[1.02] transition-all hover:shadow-orange-500/10"
                                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                                    title={`${task.name}: ${new Date(task.start).toLocaleString()} - ${new Date(task.end).toLocaleString()}`}
                                >
                                    {widthPct > 12 && (
                                        <span className="px-1 truncate font-mono">
                                            {((task.end - task.start) / (24 * 60 * 60 * 1000)).toFixed(1)}d
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Gantt Footer */}
            <div className="flex items-center justify-between border-t border-gray-800 pt-1 mt-1 text-[8px] text-gray-505 font-medium px-0.5 shrink-0">
                <div className="flex items-center gap-2 truncate">
                    {xAxisLabel && <span className="truncate">X: {xAxisLabel}</span>}
                    {yAxisLabel && <span className="truncate">Y: {yAxisLabel}</span>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <span className="text-orange-400 font-mono">
                        {new Date(timelineStart).toLocaleDateString()} - {new Date(timelineEnd).toLocaleDateString()}
                    </span>
                </div>
            </div>
        </div>
    );
}

// const CustomTooltip = ({ active, payload }: any) => {
//     if (active && payload && payload.length) {
//         return (
//             <div className="bg-gray-800/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-2.5 shadow-2xl text-[11px] text-white flex flex-col gap-1.5 min-w-[180px]">
//                 {payload.map((entry: any, index: number) => (
//                     <div key={index} className="flex justify-between items-center gap-6">
//                         <span className="text-gray-300 font-medium">{formatFieldName(entry.name)}</span>
//                         <span className="font-bold text-white font-mono">{entry.value.toLocaleString()}</span>
//                     </div>
//                 ))}
//             </div>
//         );
//     }
//     return null;
// };

// const CustomActiveDot = (props: any) => {
//     const { cx, cy, stroke } = props;
//     if (!cx || !cy) return null;
//     return (
//         <g>
//             <circle cx={cx} cy={cy} r={10} fill={stroke} fillOpacity={0.25} />
//             <circle cx={cx} cy={cy} r={4.5} fill={stroke} stroke="#ffffff" strokeWidth={1.5} />
//         </g>
//     );
// };


// ─── DataViz — EXACT same logic as VisualizePage ─────────────────────────────

function DataViz({ data, chartType, xAxisLabel: rawXAxisLabel, yAxisLabel: rawYAxisLabel, colorPalette, threshold }: { data: any; chartType: string; xAxisLabel?: string; yAxisLabel?: string; colorPalette?: string[]; threshold?: { show: boolean; value: number; color: string } }) {
    const xAxisLabel = formatFieldName(rawXAxisLabel || "");
    const yAxisLabel = formatFieldName(rawYAxisLabel || "");
    const [dotTooltip, setDotTooltip] = useState<{
        x: number;
        y: number;
        name: string;
        value: number;
        color: string;
    } | null>(null);
    const pivoted = useMemo(() => {
        if (chartType === "table" || chartType === "data_table" || chartType === "gantt") return data;
        return pivotData(data);
    }, [data, chartType]);
    if (!pivoted) return <div className="text-gray-500 text-xs p-4 flex items-center justify-center h-full">No data returned.</div>;

    const ACTIVE_COLORS = (colorPalette && colorPalette.length > 0) ? colorPalette : COLORS;
    const toHex = (c: string) => (c.startsWith("#") ? c : `#${c}`);
    const makeActiveDot = (col: string, color: string) => (props: any) => {
        const { cx, cy, value } = props;
        if (!cx || !cy) return null;
        return (
            <g
                onMouseEnter={() => setDotTooltip({ x: cx, y: cy, name: formatFieldName(col), value, color })}
                onMouseLeave={() => setDotTooltip(null)}
                style={{ cursor: 'pointer' }}
            >
                <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0} />
                <circle cx={cx} cy={cy} r={10} fill={color} fillOpacity={0.25} />
                <circle cx={cx} cy={cy} r={4.5} fill={color} stroke="#ffffff" strokeWidth={1.5} />
            </g>
        );
    };
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const filtered = payload;

            if (!filtered.length) return null;

            return (
                <div className="bg-gray-800/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-2.5 shadow-2xl text-[11px] text-white flex flex-col gap-1.5 min-w-[180px]">
                    {filtered.map((entry: any, index: number) => (
                        <div key={index} className="flex justify-between items-center gap-6">
                            <span className="flex items-center gap-1.5 text-gray-300 font-medium">
                                <span
                                    className="inline-block w-2 h-2 rounded-full shrink-0"
                                    style={{ backgroundColor: entry.color || entry.stroke }}
                                />
                                {formatFieldName(entry.name)}
                            </span>
                            <span className="font-bold text-white font-mono">
                                {entry.value?.toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (chartType === "gantt") {
        return (
            <GanttChart pivoted={pivoted} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} />
        );
    }

    if (chartType === "heatmap" || chartType === "heat_map") {
        return (
            <HeatmapChart pivoted={pivoted} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} />
        );
    }

    const columns: string[] = pivoted.columns || [];
    const rows: any[][] = pivoted.rows || [];
    if (!columns.length || !rows.length)
        return <div className="text-gray-500 text-xs p-4 flex items-center justify-center h-full">No rows returned.</div>;

    // Candlestick (Trend Analysis) View
    if (chartType === "candlestick") {
        // Try to detect OHLC columns by name, fallback to positional
        const colsLower = columns.map((c: string) => (c || "").toLowerCase());
        const oIdx = colsLower.findIndex((c: string) => c.includes("open"));
        const hIdx = colsLower.findIndex((c: string) => c.includes("high"));
        const lIdx = colsLower.findIndex((c: string) => c.includes("low"));
        const cIdx = colsLower.findIndex((c: string) => c.includes("close"));
        const vIdx = colsLower.findIndex((c: string) => c.includes("vol"));
        const hasOHLC = oIdx >= 0 && hIdx >= 0 && lIdx >= 0 && cIdx >= 0;

        interface CandleItem { label: string; open: number; high: number; low: number; close: number; volume?: number; }
        let candles: CandleItem[];

        if (hasOHLC) {
            candles = rows.map((row: any) => ({
                label: String(row[0] ?? ""),
                open: Number(row[oIdx]) || 0,
                high: Number(row[hIdx]) || 0,
                low: Number(row[lIdx]) || 0,
                close: Number(row[cIdx]) || 0,
                volume: vIdx >= 0 ? Number(row[vIdx]) || 0 : undefined,
            }));
        } else if (columns.length >= 5) {
            // Positional: [label, open, high, low, close, ?volume]
            candles = rows.map((row: any) => ({
                label: String(row[0] ?? ""),
                open: Number(row[1]) || 0,
                high: Number(row[2]) || 0,
                low: Number(row[3]) || 0,
                close: Number(row[4]) || 0,
                volume: row[5] != null ? Number(row[5]) || 0 : undefined,
            }));
        } else {
            // Not enough columns — generate dummy candlestick from available data
            candles = rows.map((row: any) => {
                const val = Number(row[1]) || Number(row[0]) || 0;
                const spread = Math.max(val * 0.1, 1);
                return {
                    label: String(row[0] ?? ""),
                    open: val - spread * 0.3,
                    high: val + spread,
                    low: val - spread,
                    close: val + spread * 0.3,
                };
            });
        }

        if (candles.length === 0) {
            return <div className="text-gray-500 text-xs p-4">No candlestick data available.</div>;
        }

        // Use a large viewBox so text renders at readable sizes
        const W = 800, H = 400;
        const MG = { top: 20, right: 60, bottom: 80, left: 50 };
        const chartW = W - MG.left - MG.right;
        const chartH = H - MG.top - MG.bottom;

        const allHighs = candles.map(c => c.high);
        const allLows = candles.map(c => c.low);
        const dataMin = Math.min(...allLows);
        const dataMax = Math.max(...allHighs);
        const range = dataMax - dataMin || 1;
        const pad = range * 0.08;
        const yMin = dataMin - pad;
        const yRange = (dataMax + pad) - yMin;

        // Y-axis ticks (5 ticks)
        const yTicks: number[] = [];
        { const s = yRange / 4; for (let i = 0; i <= 4; i++) yTicks.push(yMin + s * i); }

        // Label sampling — show at most ~12 labels to avoid overlap
        const maxLabels = 12;
        const labelStep = Math.max(1, Math.ceil(candles.length / maxLabels));

        const count = candles.length;
        const gap = chartW / count;
        const candleW = Math.min(gap * 0.6, 20);

        const toY = (val: number) => MG.top + (1 - (val - yMin) / yRange) * chartH;

        return (
            <div className="h-full w-full p-2 flex flex-col justify-center min-h-[300px]">
                <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full max-h-[380px]">
                    {/* Chart background */}
                    <rect x={MG.left} y={MG.top} width={chartW} height={chartH} fill="#0f172a" rx="4" />

                    {/* Y-Axis grid lines + labels */}
                    {yTicks.map((tick, i) => {
                        const y = toY(tick);
                        return (
                            <g key={`yt-${i}`}>
                                <line x1={MG.left} y1={y} x2={MG.left + chartW} y2={y} stroke="#1e293b" strokeWidth="1" strokeDasharray="4 4" />
                                <text x={MG.left - 8} y={y + 4} fill="#6b7280" fontSize="11" fontFamily="monospace" textAnchor="end">
                                    {tick.toFixed(tick >= 1000 ? 0 : 1)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Candles */}
                    {candles.map((candle, idx) => {
                        const cx = MG.left + gap * idx + gap / 2;
                        const isBullish = candle.close >= candle.open;
                        const bodyColor = isBullish ? "#22c55e" : "#ef4444";
                        const wickColor = isBullish ? "#16a34a" : "#dc2626";

                        const yHigh = toY(candle.high);
                        const yLow = toY(candle.low);
                        const yOpen = toY(candle.open);
                        const yClose = toY(candle.close);
                        const bodyTop = Math.min(yOpen, yClose);
                        const bodyHeight = Math.max(Math.abs(yOpen - yClose), 2);

                        return (
                            <g key={`c-${idx}`}>
                                {/* Wick */}
                                <line x1={cx} y1={yHigh} x2={cx} y2={yLow} stroke={wickColor} strokeWidth="1.5" />
                                {/* Body */}
                                <rect x={cx - candleW / 2} y={bodyTop} width={candleW} height={bodyHeight} fill={bodyColor} rx="1" stroke={wickColor} strokeWidth="0.5" />
                                {/* Hover area */}
                                <rect x={cx - gap / 2} y={MG.top} width={gap} height={chartH} fill="transparent">
                                    <title>{`${candle.label}\nO: ${candle.open}  H: ${candle.high}\nL: ${candle.low}  C: ${candle.close}${candle.volume !== undefined ? `\nVol: ${candle.volume.toLocaleString()}` : ""}`}</title>
                                </rect>
                                {/* X-axis label (sampled + rotated) */}
                                {idx % labelStep === 0 && (
                                    <text
                                        x={cx} y={MG.top + chartH + 14}
                                        fill="#9ca3af" fontSize="10" textAnchor="end" fontFamily="sans-serif"
                                        transform={`rotate(-45, ${cx}, ${MG.top + chartH + 14})`}
                                    >
                                        {candle.label.length > 12 ? candle.label.slice(0, 12) + "…" : candle.label}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {/* Chart border */}
                    <line x1={MG.left} y1={MG.top} x2={MG.left} y2={MG.top + chartH} stroke="#374151" strokeWidth="1" />
                    <line x1={MG.left} y1={MG.top + chartH} x2={MG.left + chartW} y2={MG.top + chartH} stroke="#374151" strokeWidth="1" />
                </svg>
                {/* Legend */}
                <div className="flex items-center justify-between px-1 mt-1">
                    <div className="flex items-center gap-3 text-[10px] text-gray-400">
                        <div className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500" /><span>Bullish</span></div>
                        <div className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500" /><span>Bearish</span></div>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-gray-500">
                        {xAxisLabel && <span>X: {xAxisLabel}</span>}
                        {yAxisLabel && <span>Y: {yAxisLabel}</span>}
                    </div>
                </div>
            </div>
        );
    }


    // Table
    if (chartType === "table" || chartType === "data_table") {
        return (
            <div className="overflow-auto border border-gray-700 rounded-lg h-full custom-scroll">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-gray-800/80 sticky top-0 text-gray-400 font-semibold border-b border-gray-700">
                        <tr>{columns.map(col => <th key={col} className="px-3 py-2">{col}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-gray-950/20">
                        {rows.map((row, rIdx) => (
                            <tr key={rIdx} className="hover:bg-gray-800/20 transition-colors">
                                {columns.map((_, cIdx) => (
                                    <td key={cIdx} className="px-3 py-2 text-gray-300">{formatCell(row[cIdx])}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Gauge
    if (chartType === "gauge") {
        const gaugeValue = Number(rows[0]?.[columns.length - 1] ?? rows[0]?.[0] ?? 0);
        return (
            <div className="h-full flex items-center justify-center p-2">
                <GaugeChart value={gaugeValue} label={yAxisLabel || columns[columns.length - 1] || "Value"} />
            </div>
        );
    }

    // Metric / single value
    if (chartType === "metric" || (columns.length === 1 && rows.length === 1)) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-gray-950/30 border border-gray-800 rounded-lg p-4">
                <span
                    className="text-4xl font-extrabold font-mono"
                    style={{ color: toHex(ACTIVE_COLORS[0]) }}
                >
                    {formatCell(rows[0][0])}
                </span>
                <span className="text-xs text-gray-400 mt-2 uppercase tracking-wider font-semibold text-center truncate w-full px-2">
                    {yAxisLabel || columns[0] || ""}
                </span>
            </div>
        );
    }

    // Build recharts data
    const rechartsData = rows.map(row => {
        const item: any = { name: formatFieldName(String(row[0] ?? "")) };
        columns.slice(1).forEach((col, idx) => { item[col] = Number(row[idx + 1]) || 0; });
        return item;
    });
    const valueKeys = columns.slice(1);
    const normType = normalizeChartType(chartType);

    // Pie / Donut
    if (normType === "pie") {
        const pieData = rows.map((row, i) => ({
            name: formatFieldName(String(row[0] ?? "")),
            value: Number(row[1]) || 0,
            fill: toHex(ACTIVE_COLORS[i % ACTIVE_COLORS.length]),
        }));
        const total = pieData.reduce((s, d) => s + d.value, 0);

        return (
            <div className="grid grid-cols-[1fr_180px] gap-3 h-full items-center">
                <div className="h-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <RePieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                                outerRadius="80%" innerRadius="60%" stroke="none">
                                {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </RePieChart>
                    </ResponsiveContainer>
                </div>
                <div className="max-h-full overflow-y-auto custom-scroll border border-gray-800 rounded-lg p-2 bg-gray-950/20">
                    <div className="space-y-1.5">
                        {pieData.map((item, i) => {
                            const pct = total > 0 ? `${((item.value / total) * 100).toFixed(0)}%` : "0%";
                            return (
                                <div key={i} className="flex items-center gap-2 text-[10px] text-gray-300">
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.fill }} />
                                    <span className="truncate flex-1 font-semibold">{item.name}</span>
                                    <span className="text-gray-500 font-mono shrink-0">{pct} ({item.value.toLocaleString()})</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    // Line / Area / Bar
    return (
        <div className="relative w-full h-full">
            <ResponsiveContainer width="100%" height="100%">
                {normType === "line" ? (
                    <ReLineChart data={rechartsData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} />
                        <YAxis stroke="#9ca3af" fontSize={10} />
                        {/* <Tooltip content={<CustomTooltip />} /> */}
                        <Legend verticalAlign="top" align="right" formatter={(value) => formatFieldName(value)} wrapperStyle={{ fontSize: 10, paddingBottom: 10 }} />
                        {valueKeys.map((col, i) => (
                            <Line key={col} type="monotone" dataKey={col} stroke={toHex(ACTIVE_COLORS[i % ACTIVE_COLORS.length])} strokeWidth={2} dot={{ r: 3 }} activeDot={makeActiveDot(col, toHex(ACTIVE_COLORS[i % ACTIVE_COLORS.length]))} />
                        ))}
                        {threshold?.show && (
                            <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                        )}
                    </ReLineChart>
                ) : normType === "area" ? (
                    <ReAreaChart data={rechartsData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} />
                        <YAxis stroke="#9ca3af" fontSize={10} />
                        {/* <Tooltip content={<CustomTooltip />} /> */}
                        <Legend verticalAlign="top" align="right" formatter={(value) => formatFieldName(value)} wrapperStyle={{ fontSize: 10, paddingBottom: 10 }} />
                        {valueKeys.map((col, i) => (
                            <Area key={col} type="monotone" dataKey={col} stroke={toHex(ACTIVE_COLORS[i % ACTIVE_COLORS.length])} fill={toHex(ACTIVE_COLORS[i % ACTIVE_COLORS.length])} fillOpacity={0.2} strokeWidth={2} dot={{ r: 3 }} activeDot={makeActiveDot(col, toHex(ACTIVE_COLORS[i % ACTIVE_COLORS.length]))} />
                        ))}
                        {threshold?.show && (
                            <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                        )}
                    </ReAreaChart>
                ) : (
                    <BarChart
                        data={rechartsData}
                        layout={chartType === "horizontal_bar" ? "vertical" : "horizontal"}
                        margin={{ top: 8, right: 8, left: chartType === "horizontal_bar" ? 20 : -20, bottom: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        {chartType === "horizontal_bar" ? (
                            <>
                                <XAxis type="number" stroke="#9ca3af" fontSize={10} />
                                <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={10} width={80} />
                            </>
                        ) : (
                            <>
                                <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} />
                                <YAxis stroke="#9ca3af" fontSize={10} />
                            </>
                        )}
                        <Tooltip content={<CustomTooltip />} cursor={false} />
                        <Legend verticalAlign="top" align="right" formatter={(value) => formatFieldName(value)} wrapperStyle={{ fontSize: 10, paddingBottom: 10 }} />
                        {valueKeys.map((col, i) => (
                            <Bar key={col} dataKey={col} fill={toHex(ACTIVE_COLORS[i % ACTIVE_COLORS.length])} radius={[4, 4, 0, 0]} />
                        ))}
                        {threshold?.show && (
                            chartType === "horizontal_bar" ? (
                                <ReferenceLine x={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                            ) : (
                                <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                            )
                        )}
                    </BarChart>
                )}
            </ResponsiveContainer>
            {dotTooltip && (
                <div
                    className="absolute z-50 pointer-events-none bg-gray-900/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-2.5 shadow-2xl text-[11px] text-white -translate-x-1/2 -translate-y-full"
                    style={{ left: dotTooltip.x, top: dotTooltip.y - 12 }}
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="inline-block w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: dotTooltip.color }}
                        />
                        <span className="text-gray-300 font-medium">{dotTooltip.name}</span>
                        <span className="font-bold text-white font-mono ml-4">
                            {dotTooltip.value?.toLocaleString()}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Widget Dropdown ──────────────────────────────────────────────────────────

function WidgetDropdown({
    widgets,
    selected,
    onSelect,
    loading,
}: {
    widgets: AvailableWidget[];
    selected: AvailableWidget | null;
    onSelect: (w: AvailableWidget) => void;
    loading: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const h = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const filtered = widgets.filter(
        (w) =>
            w.name.toLowerCase().includes(search.toLowerCase()) ||
            (w.chart_type || "").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="relative w-80 shrink-0" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between bg-gray-900 border border-gray-700 hover:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-left transition-colors focus:outline-none"
            >
                {loading ? (
                    <span className="flex items-center gap-2 text-gray-500 text-xs">
                        <Loader2 size={13} className="animate-spin" /> Loading widgets…
                    </span>
                ) : selected ? (
                    <span className="flex items-center gap-2 min-w-0">
                        <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border shrink-0 ${badgeClass(selected.chart_type)}`}>
                            {badgeIcon(selected.chart_type)} {selected.chart_type}
                        </span>
                        <span className="font-semibold text-white truncate text-xs">{selected.name}</span>
                    </span>
                ) : (
                    <span className="text-gray-500 text-xs">Select a widget…</span>
                )}
                <ChevronDown size={14} className={`text-gray-400 ml-2 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 flex flex-col max-h-72 overflow-hidden">
                    <div className="p-2 border-b border-gray-800 shrink-0">
                        <input
                            autoFocus
                            type="text"
                            placeholder="Search widgets…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full bg-gray-950 border border-gray-800 rounded-md px-3 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scroll py-1">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-5 text-center text-xs text-gray-600">No widgets found</div>
                        ) : (
                            filtered.map((w) => {
                                const isSel = selected?.id === w.id;
                                return (
                                    <button
                                        key={w.id}
                                        type="button"
                                        onClick={() => { onSelect(w); setOpen(false); setSearch(""); }}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-orange-500/5 transition-colors ${isSel ? "bg-orange-500/10" : ""}`}
                                    >
                                        <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border shrink-0 ${badgeClass(w.chart_type)}`}>
                                            {badgeIcon(w.chart_type)} {w.chart_type}
                                        </span>
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <span className={`text-xs font-semibold truncate ${isSel ? "text-orange-400" : "text-white"}`}>{w.name}</span>
                                            {w.description && <span className="text-[10px] text-gray-500 truncate">{w.description}</span>}
                                        </div>
                                        {isSel && <Check size={13} className="text-orange-400 shrink-0" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DashboardBuilderPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dashboardId = searchParams.get("id");

    const level = useClientLevel();
    const clientId = level.id;

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [theme, setTheme] = useState<"light" | "dark">("light");
    const [removedWidgetIds, setRemovedWidgetIds] = useState<number[]>([]);

    // Widget list
    const [availableWidgets, setAvailableWidgets] = useState<AvailableWidget[]>([]);
    const [loadingWidgets, setLoadingWidgets] = useState(true);

    // Selected widget in dropdown (just sets state — no API call until Add is clicked)
    const [selectedWidget, setSelectedWidget] = useState<AvailableWidget | null>(null);

    // Canvas
    const [tiles, setTiles] = useState<CanvasTile[]>([]);
    const [layouts, setLayouts] = useState<ResponsiveLayouts>({ lg: [], md: [], sm: [], xs: [], xxs: [] });

    // ── Fetch widget list ──────────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            setLoadingWidgets(true);
            try {
                const res = await baseapi.get("/analytics/widgets", { params: { client_id: clientId } });
                const list = res.data?.data !== undefined ? res.data.data : Array.isArray(res.data) ? res.data : [];
                setAvailableWidgets(list);
            } catch (err: any) {
                showError(err.message || "Failed to load widgets");
            } finally {
                setLoadingWidgets(false);
            }
        })();
    }, [clientId]);

    useEffect(() => {
        if (!dashboardId) return;

        (async () => {
            setLoadingDashboard(true);
            try {
                const res = await baseapi.get(`/analytics/dashboards/${dashboardId}`, {
                    params: { client_id: clientId },
                });
                const dash = res.data?.data || res.data;
                setName(dash.name || "");
                setDescription(dash.description || "");
                setTheme(dash.settings?.theme || "light");

                const widgets = dash.widgets || [];
                // Initialize tiles as loading placeholders
                const initialTiles = widgets.map((w: any) => ({
                    instanceKey: String(w.id),
                    widget: {
                        id: w.id,
                        name: w.name,
                        description: w.description,
                        chart_type: w.chart_type,
                        query_definition: w.query_definition,
                        visualization_config: w.visualization_config,
                    },
                    queryResult: null,
                    loading: true,
                }));
                setTiles(initialTiles);

                const gridItems = widgets.map((w: any) => ({
                    i: String(w.id),
                    x: w.layout?.x ?? 0,
                    y: w.layout?.y ?? 0,
                    w: w.layout?.width ?? 6,
                    h: w.layout?.height ?? 4,
                    minW: 2,
                    minH: 3
                }));

                setLayouts({
                    lg: gridItems,
                    md: gridItems,
                    sm: gridItems,
                    xs: gridItems,
                    xxs: gridItems
                });

                // Fetch query data in parallel for each widget
                await Promise.all(
                    widgets.map(async (w: any) => {
                        try {
                            let qd = w.query_definition;
                            if (typeof qd === "string") {
                                try { qd = JSON.parse(qd); } catch { /* noop */ }
                            }
                            if (qd) {
                                const queryRes = await baseapi.post("/analytics/query", qd, {
                                    params: { client_id: clientId },
                                });
                                const qData = queryRes.data?.data !== undefined ? queryRes.data.data : queryRes.data;
                                setTiles(prev => prev.map(t =>
                                    t.instanceKey === String(w.id) ? { ...t, queryResult: qData, loading: false } : t
                                ));
                            } else {
                                setTiles(prev => prev.map(t =>
                                    t.instanceKey === String(w.id) ? { ...t, loading: false } : t
                                ));
                            }
                        } catch (err) {
                            console.error(`Failed to load data for widget ${w.id}:`, err);
                            setTiles(prev => prev.map(t =>
                                t.instanceKey === String(w.id) ? { ...t, loading: false } : t
                            ));
                        }
                    })
                );
            } catch (err: any) {
                showError(err.message || "Failed to load dashboard details");
            } finally {
                setLoadingDashboard(false);
            }
        })();
    }, [dashboardId, clientId]);

    // ── Select widget — just update state, no API call yet ───────────────────
    const handleSelectWidget = (w: AvailableWidget) => {
        setSelectedWidget(w);
    };

    // ── Add widget to canvas — places tile immediately, fetches data async ────
    const handleAddToCanvas = async () => {
        if (!selectedWidget) return;

        const key = uid();
        const col = (tiles.length * 6) % 12;

        // 1) Place tile immediately with loading spinner
        const placeholderTile: CanvasTile = {
            instanceKey: key,
            widget: selectedWidget,
            queryResult: null,
            loading: true,
        };
        setTiles((prev) => [...prev, placeholderTile]);
        setLayouts((prev) => {
            const item: GridItem = { i: key, x: col, y: Infinity, w: 6, h: 6, minW: 2, minH: 3 };
            const updated: ResponsiveLayouts = {};
            for (const bp of ["lg", "md", "sm", "xs", "xxs"] as const) {
                updated[bp] = [...(prev[bp] || []), item];
            }
            return updated;
        });

        // 2) Fetch full widget + run query in background
        try {
            const widgetRes = await baseapi.get(`/analytics/widgets/${selectedWidget.id}`, {
                params: { client_id: clientId },
            });
            const fullWidget: AvailableWidget = widgetRes.data?.data || widgetRes.data;

            let queryData: any = null;
            if (fullWidget?.query_definition) {
                const queryRes = await baseapi.post("/analytics/query", fullWidget.query_definition, {
                    params: { client_id: clientId },
                });
                queryData = queryRes.data?.data !== undefined ? queryRes.data.data : queryRes.data;
            }

            // 3) Update tile with real data
            setTiles((prev) =>
                prev.map((t) =>
                    t.instanceKey === key
                        ? { ...t, widget: fullWidget, queryResult: queryData, loading: false }
                        : t
                )
            );
        } catch (err: any) {
            console.error("Failed to load widget data:", err);
            setTiles((prev) =>
                prev.map((t) =>
                    t.instanceKey === key ? { ...t, loading: false } : t
                )
            );
        }
    };

    // ── Remove tile ───────────────────────────────────────────────────────────
    const handleRemoveTile = (key: string) => {
        const tileToRemove = tiles.find(t => t.instanceKey === key);
        if (tileToRemove && tileToRemove.widget.id) {
            setRemovedWidgetIds(prev => [...prev, tileToRemove.widget.id]);
        }
        setTiles((prev) => prev.filter((t) => t.instanceKey !== key));
        setLayouts((prev) => {
            const updated: ResponsiveLayouts = {};
            for (const bp of ["lg", "md", "sm", "xs", "xxs"] as const) {
                updated[bp] = (prev[bp] || []).filter((l) => l.i !== key);
            }
            return updated;
        });
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!name.trim()) { showError("Please enter a dashboard name."); return; }
        if (tiles.length === 0) { showError("Add at least one widget before saving."); return; }

        setSaving(true);
        try {
            const lgLayouts = layouts.lg || [];

            // Build widgets array — each tile mapped to API shape
            const widgetsPayload = tiles.map((tile, index) => {
                const layout = lgLayouts.find((l) => l.i === tile.instanceKey);
                return {
                    widget_id: tile.widget.id,
                    x: layout?.x ?? (index * 6) % 12,
                    y: layout?.y ?? 0,
                    width: layout?.w ?? 6,
                    height: layout?.h ?? 6,
                };
            });

            const payload: any = {
                name: name.trim(),
                description: description.trim(),
                client_id: clientId || null,
                is_public: false,
                settings: {
                    show_filter_bar: true,
                    refresh_interval: 60,
                    grid_columns: 12,
                    row_height: 150,
                    theme: theme,
                    time_range: { field: "created", from: "now-7d", to: "now" },
                    filters: [],
                },
                widgets: widgetsPayload,
            };

            if (dashboardId) {
                if (removedWidgetIds.length > 0) {
                    payload.remove_widget_ids = removedWidgetIds;
                }
                await baseapi.put(`/analytics/dashboards/${dashboardId}`, payload);
                showSuccess("Dashboard updated successfully!");
            } else {
                await baseapi.post("/analytics/dashboards", payload);
                showSuccess("Dashboard created successfully!");
            }

            navigate("/custom-dashboard");
        } catch (err: any) {
            showError(err.message || "Failed to save dashboard");
        } finally {
            setSaving(false);
        }
    };


    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col h-full bg-gray-950 text-gray-200 overflow-hidden pb-20">

            {/* ── Top bar ─────────────────────────────────────────────────── */}
            <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-gray-800 bg-gray-900/70 backdrop-blur-sm">
                <button
                    onClick={() => navigate("/custom-dashboard")}
                    className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors shrink-0"
                    title="Back to Dashboards"
                >
                    <ArrowLeft size={16} />
                </button>
                <div className="p-2 rounded-lg bg-orange-600 shrink-0">
                    <LayoutDashboard size={16} className="text-white" />
                </div>
                <input
                    type="text"
                    placeholder="Dashboard name…"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="flex-1 max-w-xs bg-gray-800 border border-gray-700 focus:border-orange-500 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none transition-colors font-semibold"
                />
                <input
                    type="text"
                    placeholder="Description (optional)…"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="flex-1 max-w-sm bg-gray-800 border border-gray-700 focus:border-orange-500 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none transition-colors"
                />
                <div className="flex-1" />
                <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1 shrink-0 select-none">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Theme:</span>
                    <button
                        type="button"
                        onClick={() => setTheme(prev => prev === "light" ? "dark" : "light")}
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase transition-all ${theme === "dark"
                            ? "bg-orange-600 text-white"
                            : "bg-gray-700 text-gray-300 hover:text-white"
                            }`}
                    >
                        {theme}
                    </button>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving || !name.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg border border-orange-500 transition-colors shrink-0"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {saving ? (dashboardId ? "Update Dashboard" : "Save Dashboard") : (dashboardId ? "Update Dashboard" : "Save Dashboard")}
                </button>
            </div>

            {loadingDashboard ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-3">
                    <Loader2 size={32} className="animate-spin text-orange-500/70" />
                    <span className="text-sm text-gray-500 font-medium">Loading dashboard layout…</span>
                </div>
            ) : (
                <>
                    {/* ── Widget selector bar ─────────────────────────────────────── */}
                    <div className="shrink-0 flex items-center gap-3 px-4 py-2.5 border-b border-gray-800 bg-gray-900/50">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider shrink-0">
                            <Layers size={13} className="text-orange-400" />
                            Add Widget
                        </div>
                        <WidgetDropdown
                            widgets={availableWidgets}
                            selected={selectedWidget}
                            onSelect={handleSelectWidget}
                            loading={loadingWidgets}
                        />
                        <button
                            onClick={handleAddToCanvas}
                            disabled={!selectedWidget}
                            className="flex items-center gap-1.5 px-3 py-2 bg-orange-600/10 hover:bg-orange-600/20 disabled:opacity-40 disabled:cursor-not-allowed text-orange-400 text-xs font-semibold rounded-lg border border-orange-500/30 transition-colors shrink-0"
                        >
                            <Plus size={14} /> Add to Canvas
                        </button>
                        {tiles.length > 0 && (
                            <span className="text-[11px] text-gray-500 font-medium">
                                {tiles.length} widget{tiles.length !== 1 ? "s" : ""} on canvas
                            </span>
                        )}
                        <div className="flex-1" />
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-600 font-medium">
                            <Info size={11} /> Drag to move · Drag edges to resize
                        </div>
                    </div>

                    {/* ── Canvas — full width, no preview panel ─────────────────── */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scroll p-4 bg-gray-950">
                        {tiles.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full min-h-[400px] border-2 border-dashed border-gray-800 rounded-2xl text-center px-6 select-none">
                                <div className="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4">
                                    <LayoutDashboard size={26} className="text-orange-400" />
                                </div>
                                <p className="text-white font-bold text-base mb-1">Canvas is empty</p>
                                <p className="text-gray-500 text-sm max-w-xs">
                                    Select a widget from the dropdown above and click{" "}
                                    <strong className="text-gray-400">Add to Canvas</strong>.
                                </p>
                            </div>
                        ) : (
                            <ResponsiveGridLayout
                                className="layout"
                                layouts={layouts}
                                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                                rowHeight={60}
                                margin={[12, 12]}
                                containerPadding={[0, 0]}
                                isDraggable
                                isResizable
                                resizeHandles={["s", "w", "e", "n", "sw", "nw", "se", "ne"]}
                                onLayoutChange={(_cur, all) => setLayouts(all)}
                                draggableHandle=".drag-handle"
                            >
                                {tiles.map((tile) => (
                                    <div key={tile.instanceKey}>
                                        <div className="h-full flex flex-col bg-gray-900 border border-gray-700 hover:border-gray-600 rounded-xl overflow-hidden shadow-sm transition-colors group">
                                            {/* Tile header */}
                                            <div className="drag-handle flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-gray-800/60 cursor-grab active:cursor-grabbing shrink-0 select-none">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className={`flex items-center gap-1 px-1.5 py-0.5  `}>
                                                        {badgeIcon(tile.widget.chart_type)}
                                                    </span>
                                                    <span className="text-xs font-bold text-white truncate" title={tile.widget.name}>
                                                        {tile.widget.name}
                                                    </span>
                                                </div>
                                                <button
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={() => handleRemoveTile(tile.instanceKey)}
                                                    className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-all opacity-0 group-hover:opacity-100 shrink-0 ml-1"
                                                    title="Remove widget"
                                                >
                                                    <X size={13} />
                                                </button>
                                            </div>

                                            {/* Tile body */}
                                            <div className="flex-1 p-2 min-h-0 overflow-hidden">
                                                {tile.loading ? (
                                                    <div className="h-full flex flex-col items-center justify-center gap-2">
                                                        <Loader2 size={20} className="animate-spin text-orange-500/60" />
                                                        <span className="text-[10px] text-gray-600">Loading data…</span>
                                                    </div>
                                                ) : tile.queryResult ? (
                                                    (() => {
                                                        const { xAxisLabel, yAxisLabel } = getAxisLabels(tile.widget);
                                                        const visConfig = safeParse(tile.widget?.visualization_config) || {};
                                                        const colorPalette = visConfig?.color_palette ?? [];
                                                        return (
                                                            <DataViz
                                                                data={tile.queryResult}
                                                                chartType={tile.widget.chart_type}
                                                                xAxisLabel={xAxisLabel}
                                                                yAxisLabel={yAxisLabel}
                                                                colorPalette={colorPalette}
                                                                threshold={visConfig?.threshold}
                                                            />
                                                        );
                                                    })()
                                                ) : (
                                                    <div className="h-full flex items-center justify-center text-xs text-gray-600">
                                                        No data
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </ResponsiveGridLayout>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
